import React, { useState, useEffect, useMemo, useCallback } from "react";
import { getEnquiriesPage } from "../../services/EnquiryService.js";
import { Filter } from "lucide-react";
import {StatusBadge} from "../SharedInputs.jsx";

function EnquiryList({ onSelectEnquiry }) {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [activeStatus, setActiveStatus] = useState("new");
  const [lastCreatedAt, setLastCreatedAt] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchEnquiries = useCallback(
    async (reset = false) => {
        setLoading(true);
        try {
          const data = await getEnquiriesPage({
              limit: 10,
              startAfter: reset ? null : lastCreatedAt,
              searchText,
          });

          const enquiryData = Array.isArray(data) ? data : data?.data || [];

          if (reset) {
              setEnquiries(enquiryData);
          } else {
              setEnquiries((prev) => [...prev, ...enquiryData]);
          }

          if (enquiryData.length > 0 && enquiryData[enquiryData.length - 1]?.createdAt) {
              setLastCreatedAt(enquiryData[enquiryData.length - 1].createdAt);
          }

        setHasMore(enquiryData.length === 10);
        } catch (err) {
          alert("Failed to load enquiries");
        } finally {
          setLoading(false);
        }
    },
    [lastCreatedAt, searchText] // dependencies used inside
    );

  useEffect(() => {
    fetchEnquiries(true); // fetch first page
  }, [searchText]);

  // Count enquiries by status
  const statusCounts = useMemo(() => {
    const counts = { new: 0, resolved: 0, all: enquiries.length };
    enquiries.forEach((e) => {
      const s = e.status || "new";
      counts[s] = (counts[s] ?? 0) + 1;
    });
    return counts;
  }, [enquiries]);

  const filteredEnquiries = useMemo(() => {
    return enquiries.filter((e) => {
      const matchStatus = activeStatus === "all" ? true : e.status === activeStatus;
      const matchSearch =
        e.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
        e.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
        e.email?.toLowerCase().includes(searchText.toLowerCase());
      return matchStatus && matchSearch;
    });
  }, [enquiries, activeStatus, searchText]);

  const barWrap = { display: "flex", justifyContent: "flex-end", marginBottom: 16 };
  const chip = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "6px 10px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  };
  const labelStyle = { fontSize: 13, color: "#6b7280", fontWeight: 600 };
  const selectStyle = {
    border: "none",
    background: "transparent",
    fontSize: 14,
    outline: "none",
    padding: "4px 6px",
    cursor: "pointer",
  };

  return (
    <div className="record-list">
      <div style={{ display: "flex", justifyContent: "end", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
        {/* Search */}
        {/* <input
          type="text"
          placeholder="Search by Name or Email..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="form-input"
          style={{ maxWidth: "300px" }}
        /> */}
        {/* Status Filter */}
        <div style={{...barWrap, alignItems: "center", marginBottom: 0} }>
          <div style={chip}>
            <Filter size={16} color="#6b7280" />
            <span style={labelStyle}>Status</span>
            <select
              value={activeStatus}
              onChange={(e) => setActiveStatus(e.target.value)}
              style={selectStyle}
            >
              <option value="all">All ({statusCounts.all})</option>
              <option value="new">New ({statusCounts.new || 0})</option>
              <option value="resolved">Resolved ({statusCounts.resolved || 0})</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading && enquiries.length === 0 ? (
        <p>Loading enquiries...</p>
      ) : (
        <table className="record-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #ddd" }}>
              <th style={{ padding: "8px", textAlign: "center" }}>Name</th>
              <th style={{ padding: "8px", textAlign: "center" }}>Email</th>
              <th style={{ padding: "8px", textAlign: "center" }}>Phone</th>
              <th style={{ padding: "8px", textAlign: "center" }}>Agent</th>
              <th style={{ padding: "8px", textAlign: "center" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredEnquiries.map((enquiry) => (
              <tr
                key={enquiry.id}
                onClick={() => onSelectEnquiry(enquiry)}
                style={{ cursor: "pointer", borderBottom: "1px solid #eee" }}
              >
                <td style={{ padding: "8px", textAlign: "center" }}>
                  {enquiry.firstName} {enquiry.lastName}
                </td>
                <td style={{ padding: "8px", textAlign: "center" }}>{enquiry.email}</td>
                <td style={{ padding: "8px", textAlign: "center" }}>{enquiry.phone}</td>
                <td style={{ padding: "8px", textAlign: "center" }}>{enquiry.agent}</td>
                <td style={{ padding: "8px", textAlign: "center" }}><StatusBadge status={enquiry.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Load More */}
      {hasMore && (
        <div style={{ marginTop: "1rem", textAlign: "center" }}>
          <button className="btn btn-secondary" onClick={() => fetchEnquiries(false)}>
            Load More
          </button>
        </div>
      )}
    </div>
  );
}

export default EnquiryList;
